"use client";

import React from "react";
import { ArrowRight, SlidersHorizontal, Gauge, Layers, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { type L6 } from "@/lib/commonCopy";
interface CriteriaItem {
  id: number;
  title: L6;
  description: L6;
  icon: React.ComponentType<{
    className?: string;
    "aria-hidden"?: string | boolean;
  }>;
  metric: L6;
}
const criteriaPoints: CriteriaItem[] = [{
  id: 1,
  title: {
    en: "Micron rating & aperture size",
    zh: "过滤精度与孔径尺寸",
    es: "Grado de micras y tamaño de abertura",
    de: "Filterfeinheit & Maschenweite",
    fr: "Seuil de filtration & taille d'ouverture",
    pt: "Classificação em mícrons e tamanho de abertura"
  },
  description: {
    en: "Determine exact retention thresholds from 1 µm woven cloth to 10 mm coarse screens for particle capture.",
    zh: "精确确定拦截阈值，覆盖从 1 µm 精密滤布到 10 mm 粗滤筛网的微粒捕集需求。",
    es: "Determine los umbrales exactos de retención desde tela tejida de 1 µm hasta cribas gruesas de 10 mm para captura de partículas.",
    de: "Bestimmen Sie exakte Rückhaltegrenzen von 1 µm Gewebetuch bis zu 10 mm Grobsieben für die Partikelabscheidung.",
    fr: "Déterminez les seuils de rétention exacts, de la toile tissée 1 µm aux grilles grossières de 10 mm pour la capture particulaire.",
    pt: "Determine limites exatos de retenção, desde tecido de 1 µm até telas grossas de 10 mm para captura de partículas."
  },
  icon: SlidersHorizontal,
  metric: {
    en: "1 µm – 10,000 µm",
    zh: "1 µm – 10,000 µm",
    es: "1 µm – 10.000 µm",
    de: "1 µm – 10.000 µm",
    fr: "1 µm – 10 000 µm",
    pt: "1 µm – 10.000 µm"
  }
}, {
  id: 2,
  title: {
    en: "Differential pressure & flow rate",
    zh: "压降压差与流体通量",
    es: "Presión diferencial y caudal",
    de: "Differenzdruck & Durchflussrate",
    fr: "Pression différentielle & débit",
    pt: "Pressão diferencial e vazão"
  },
  description: {
    en: "Calculate media collapse pressure and clean delta P across multi-layer sintered or mesh assemblies.",
    zh: "计算过滤介质初始洁净压降 (ΔP) 及多层烧结网或滤芯组件的抗压溃极限。",
    es: "Calcule la presión de colapso del medio y la delta P limpia en conjuntos de malla o sinterizados multicapa.",
    de: "Berechnen Sie den Kollapsdruck des Mediums und das Rein-Delta-P über mehrlagige Sinter- oder Gewebebaugruppen.",
    fr: "Calculez la pression de collapsus du média et le delta P propre sur les assemblages multicouches frittés ou toiles.",
    pt: "Calcule a pressão de colapso do meio filtrante e o delta P limpo em conjuntos multicamadas sinterizados ou telas."
  },
  icon: Gauge,
  metric: {
    en: "High differential pressure ΔP rating",
    zh: "高压降 ΔP 耐受能力",
    es: "Alta resistencia a presión diferencial ΔP",
    de: "Hohe Differenzdruckbelastbarkeit ΔP",
    fr: "Haute tenue en pression différentielle ΔP",
    pt: "Alta resistência à pressão diferencial ΔP"
  }
}, {
  id: 3,
  title: {
    en: "Alloy & thermal stability",
    zh: "合金材质与热稳定性",
    es: "Aleación y estabilidad térmica",
    de: "Legierung & thermische Stabilität",
    fr: "Alliage & stabilité thermique",
    pt: "Liga e estabilidade térmica"
  },
  description: {
    en: "Match corrosion exposure and operating temperatures with standard 304, 304L, 316, or 316L stainless steels.",
    zh: "根据腐蚀介质与工作温度匹配 304、304L、316 或 316L 等标准不锈钢牌号。",
    es: "Adapte la exposición a la corrosión y las temperaturas operativas con aceros inoxidables estándar 304, 304L, 316 o 316L.",
    de: "Stimmen Sie Korrosionsexposition und Betriebstemperaturen mit den Standardedelstählen 304, 304L, 316 oder 316L ab.",
    fr: "Associez l'exposition à la corrosion et les températures de service avec les aciers inoxydables standard 304, 304L, 316 ou 316L.",
    pt: "Combine a exposição à corrosão e as temperaturas operacionais com os aços inoxidáveis padrão 304, 304L, 316 ou 316L."
  },
  icon: ShieldCheck,
  metric: {
    en: "High temperature stability",
    zh: "高温热稳定性",
    es: "Estabilidad a alta temperatura",
    de: "Hochtemperaturbeständigkeit",
    fr: "Stabilité à haute température",
    pt: "Estabilidade a alta temperatura"
  }
}, {
  id: 4,
  title: {
    en: "Structural media geometry",
    zh: "滤材结构与几何形态",
    es: "Geometría estructural del medio",
    de: "Strukturelle Mediengeometrie",
    fr: "Géométrie structurelle du média",
    pt: "Geometria estrutural do meio"
  },
  description: {
    en: "Specify sintered laminates, pleated elements, cylindrical cartridges, or edge-welded filter packs.",
    zh: "按需选定烧结层合板、折叠滤芯、圆柱形滤筒或包边焊接组合滤网。",
    es: "Especifique laminados sinterizados, elementos plisados, cartuchos cilíndricos o paquetes filtrantes soldados en los bordes.",
    de: "Spezifizieren Sie Sinterlaminate, Plissee-Elemente, zylindrische Kerzen oder randgeschweißte Filterpakete.",
    fr: "Spécifiez des stratifiés frittés, éléments plissés, cartouches cylindriques ou paquets filtrants soudés sur les bords.",
    pt: "Especifique laminados sinterizados, elementos plissados, cartuchos cilíndricos ou pacotes filtrantes com bordas soldadas."
  },
  icon: Layers,
  metric: {
    en: "Single & 5-Layer",
    zh: "单层及 5 层烧结",
    es: "Monocapa y 5 capas",
    de: "Einlagig & 5-lagig",
    fr: "Monocouche & 5 couches",
    pt: "Monocamada e 5 camadas"
  }
}];
const copy = {
  frameworkBadge: {
    en: "Technical Engineering Framework",
    zh: "技术工程选型框架",
    es: "Marco de ingeniería técnica",
    de: "Technischer Engineering-Leitfaden",
    fr: "Cadre d'ingénierie technique",
    pt: "Estrutura de engenharia técnica"
  },
  h2: {
    en: "Core engineering criteria for component specification",
    zh: "组件规格的核心工程选型标准",
    es: "Criterios clave de ingeniería para la especificación de componentes",
    de: "Zentrale Engineering-Kriterien für die Komponentenauslegung",
    fr: "Critères d'ingénierie essentiels pour la spécification des composants",
    pt: "Critérios essenciais de engenharia para especificação de componentes"
  },
  lead: {
    en: "Review key mechanical, thermal, and fluid dynamics criteria before ordering stainless steel wire mesh, sintered laminates, or custom filter elements.",
    zh: "在订购不锈钢编织丝网、多层烧结复合网或定制滤芯组件前，全面评估机械强度、耐温极限及流体动力学等核心技术指标。",
    es: "Revise los criterios clave de mecánica, temperatura y dinámica de fluidos antes de ordenar malla de alambre de acero inoxidable, laminados sinterizados o elementos filtrantes a medida.",
    de: "Prüfen Sie wesentliche mechanische, thermische und strömungstechnische Kriterien vor der Bestellung von Edelstahldrahtgewebe, Sinterlaminaten oder individuellen Filterelementen.",
    fr: "Examinez les critères mécaniques, thermiques et de dynamique des fluides essentiels avant de commander de la toile métallique inox, des stratifiés frittés ou des éléments filtrants sur mesure.",
    pt: "Avalie os principais critérios mecânicos, térmicos e de dinâmica de fluidos antes de encomendar malhas de aço inoxidável, laminados sinterizados ou elementos filtrantes personalizados."
  },
  stripCad: {
    en: "CAD & Micron Rating Data",
    zh: "CAD 图纸与过滤精度数据",
    es: "Planos CAD y datos de micras",
    de: "CAD-Daten & Filterfeinheiten",
    fr: "Données CAD & seuils de filtration",
    pt: "Dados CAD e micragem"
  },
  stripTolerances: {
    en: "Standard & Custom OEM Tolerances",
    zh: "标准及定制 OEM 公差控制",
    es: "Tolerancias estándar y OEM a medida",
    de: "Standard- & OEM-Toleranzen",
    fr: "Tolérances standard & OEM sur mesure",
    pt: "Tolerâncias padrão e OEM personalizadas"
  },
  stripB2b: {
    en: "Direct B2B Supply",
    zh: "B2B 源头工厂直供",
    es: "Suministro directo B2B",
    de: "Direkte B2B-Lieferung",
    fr: "Approvisionnement direct B2B",
    pt: "Fornecimento B2B direto"
  },
  criterionLabel: {
    en: "Criterion",
    zh: "评估标准",
    es: "Criterio",
    de: "Kriterium",
    fr: "Critère",
    pt: "Critério"
  },
  detailsLink: {
    en: "Technical details",
    zh: "技术详情",
    es: "Detalles técnicos",
    de: "Technische Details",
    fr: "Détails techniques",
    pt: "Detalhes técnicos"
  },
  customBannerTitle: {
    en: "Need custom engineering?",
    zh: "需要非标定制工程服务？",
    es: "¿Necesita ingeniería a medida?",
    de: "Individuelle Auslegung erforderlich?",
    fr: "Besoin d'ingénierie sur mesure ?",
    pt: "Precisa de engenharia sob medida?"
  },
  customBannerDesc: {
    en: "Submit fluid parameters, operating pressure, and mesh drawings to our engineering team.",
    zh: "提交流体物性参数、工作压差以及丝网图纸即可开始工程审核。",
    es: "Envíe los parámetros del fluido, la presión operativa y los planos de la malla a nuestro equipo de ingeniería.",
    de: "Übermitteln Sie Fluidparameter, Betriebsdruck und Gewebezeichnungen an unser Ingenieurteam.",
    fr: "Transmettez les paramètres du fluide, la pression de service et les plans de toile à notre équipe d'ingénierie.",
    pt: "Envie os parâmetros do fluido, a pressão operacional e os desenhos da malha para nossa equipe de engenharia."
  },
  consultEngineerLink: {
    en: "Consult an engineer",
    zh: "咨询工程人员",
    es: "Consultar a un ingeniero",
    de: "Ingenieur konsultieren",
    fr: "Consulter un ingénieur",
    pt: "Consultar um engenheiro"
  }
};
export default function FilterCriteria() {
  const {
    lang
  } = useLanguage();
  return <section id="filtercriteria" data-nav="light" className="relative overflow-hidden bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Heading & Framework description */}
          <div className="lg:col-span-5 flex flex-col justify-start">
            <div className="mb-4 inline-flex items-center gap-1.5 self-start rounded-md border border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
              <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
              <span>{pick(lang, copy.frameworkBadge)}</span>
            </div>

            <h2 className="mf-h2 font-default text-foreground text-balance">
              {pick(lang, copy.h2)}
            </h2>

            <p className="mt-4 mf-lead text-muted-foreground leading-relaxed text-balance">
              {pick(lang, copy.lead)}
            </p>

            <div className="mt-6 pt-5 border-t border-border flex flex-wrap items-center gap-4 mf-caption text-muted-foreground">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="size-4 text-primary" aria-hidden="true" />
                <span>{pick(lang, copy.stripCad)}</span>
              </div>
              <span>•</span>
              <div>{pick(lang, copy.stripTolerances)}</div>
              <span>•</span>
              <div>{pick(lang, copy.stripB2b)}</div>
            </div>

            {/* Sub-strip info box */}
            <div className="mt-8 rounded-lg border border-border bg-muted/40 p-4">
              <p className="text-sm font-semibold text-foreground">
                {pick(lang, copy.customBannerTitle)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {pick(lang, copy.customBannerDesc)}
              </p>
              <div className="mt-3">
                <Link to="/ContactPage" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                  <span>{pick(lang, copy.consultEngineerLink)}</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>

          {/* Right Column: Key Criteria Cards */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {criteriaPoints.map((item, index) => {
              const IconComponent = item.icon;
              return <Card key={item.id} data-index={index} className="relative overflow-hidden border border-border bg-card hover:border-primary/40 transition-[border-color]">
                    <CardContent className="p-5 flex flex-col justify-between h-full">
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                            <IconComponent className="size-5" aria-hidden="true" />
                          </div>
                          <span className="font-mono mf-caption font-semibold px-2 py-0.5 rounded-sm bg-muted text-foreground">
                            {pick(lang, item.metric)}
                          </span>
                        </div>
                        <h3 className="font-default font-semibold text-foreground text-base tracking-tight mb-2">
                          {pick(lang, item.title)}
                        </h3>
                        <p className="mf-caption text-muted-foreground leading-normal">
                          {pick(lang, item.description)}
                        </p>
                      </div>
                      <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                        <span className="mf-caption font-medium text-muted-foreground">
                          {`${pick(lang, copy.criterionLabel)} 0${item.id}`}
                        </span>
                        <Link to="/TechnicalResourcesPage" className="mf-caption font-medium text-primary hover:underline inline-flex items-center gap-1">
                          <span>{pick(lang, copy.detailsLink)}</span>
                          <ArrowRight className="size-3.5" aria-hidden="true" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}